import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    FolderOpen,
    Upload,
    Download,
    Trash2,
    ExternalLink,
    Loader2,
    File as FileIcon,
    Image as ImageIcon,
    FileText,
    Film,
    Music,
    Camera,
    Receipt,
    Plus,
    DollarSign,
    CalendarDays,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
    listProjectFiles,
    uploadProjectFile,
    deleteProjectFile,
    fetchProjectFileBlobUrl,
    addManualExpense,
} from "../lib/api";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "./ui/sheet";

/**
 * Per-project file folder — any file type. Backed by `project_files`
 * collection + the same object storage helpers the rest of the app uses.
 * Visible to the project owner AND any workgroup member if the project
 * has been shared to a workgroup.
 *
 * Actions per row: Open (new tab), Download, Delete.
 *
 * Delete permission policy (mirrors backend):
 *   - own files: always deletable
 *   - others' files: only the project owner or a workgroup admin can
 *     delete; the backend enforces this so the button just relies on a
 *     friendly error toast when the API says 403.
 */
function fileIcon(mime) {
    const m = (mime || "").toLowerCase();
    if (m.startsWith("image/")) return ImageIcon;
    if (m.startsWith("video/")) return Film;
    if (m.startsWith("audio/")) return Music;
    if (m.includes("pdf") || m.includes("word") || m.includes("text") || m.includes("excel") || m.includes("spreadsheet")) return FileText;
    return FileIcon;
}

function fmtSize(n) {
    if (n == null) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
    } catch {
        return "";
    }
}

function fmtMoney(amount, currency) {
    if (amount == null) return "—";
    const c = (currency || "USD").toUpperCase();
    try {
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: c,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return `${c} ${Number(amount).toFixed(2)}`;
    }
}

// iter-98: per-kind copy + chrome so the same panel renders cleanly
// whether the user opened Files, Photos, or Receipts.
const KIND_META = {
    file: {
        title: "Project files",
        Icon: FolderOpen,
        description: "Drop any file here — images, documents, plans, anything. Shared with everyone in this project's workgroup. Photos and receipts also appear here.",
        emptyText: "No files yet. Click upload to add the first one.",
        uploadLabel: "Upload files",
        kindForList: null, // null = show ALL kinds (files + photos + receipts)
        kindForUpload: "file",
        layout: "list",
    },
    photo: {
        title: "Photos",
        Icon: Camera,
        description: "Job-site photos. Snap progress shots, document existing conditions, before/after shots.",
        emptyText: "No photos yet. Upload your first one.",
        uploadLabel: "Upload photos",
        kindForList: "photo",
        kindForUpload: "photo",
        layout: "grid",
        accept: "image/*",
    },
    receipt: {
        title: "Receipts",
        Icon: Receipt,
        description: "Receipts for materials, fuel, sub-contractors, anything related to this job. Upload images/PDFs, or add an entry manually if you don't have a receipt.",
        emptyText: "No receipts yet. Upload one or add a manual expense.",
        uploadLabel: "Upload receipts",
        kindForList: "receipt",
        kindForUpload: "receipt",
        layout: "grid",
        accept: "image/*,application/pdf",
        // iter-100: only receipts get the "Add expense" button — photos
        // and generic files don't need structured amount/date metadata.
        allowManual: true,
    },
};

export default function ProjectFilesPanel({ projectId, open, onOpenChange, kind = "file" }) {
    const meta = KIND_META[kind] || KIND_META.file;
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadingPct, setUploadingPct] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [thumbs, setThumbs] = useState({}); // {fileId: blobUrl}
    const [manualOpen, setManualOpen] = useState(false);
    const fileInputRef = useRef(null);

    const refresh = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const list = await listProjectFiles(projectId, meta.kindForList);
            setFiles(list);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load files");
        } finally {
            setLoading(false);
        }
    }, [projectId, meta.kindForList]);

    useEffect(() => {
        if (open) refresh();
    }, [open, refresh]);

    // iter-98: in grid mode, lazy-fetch image thumbnails via the
    // authenticated download endpoint so we don't have to expose tokens
    // in URL query strings.
    useEffect(() => {
        if (meta.layout !== "grid" || !open) return;
        let cancelled = false;
        (async () => {
            for (const f of files) {
                if (cancelled) return;
                if (thumbs[f.id]) continue;
                if (!(f.mime || "").startsWith("image/")) continue;
                try {
                    const url = await fetchProjectFileBlobUrl(projectId, f.id);
                    if (cancelled) {
                        URL.revokeObjectURL(url);
                        return;
                    }
                    setThumbs((t) => ({ ...t, [f.id]: url }));
                } catch {
                    // silently skip — placeholder icon will render
                }
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files, meta.layout, open, projectId]);

    // Revoke blob URLs when panel closes so memory doesn't pile up.
    useEffect(() => {
        if (!open) {
            Object.values(thumbs).forEach((u) => {
                try { URL.revokeObjectURL(u); } catch {}
            });
            setThumbs({});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleUpload = async (e) => {
        const list = Array.from(e.target.files || []);
        e.target.value = ""; // allow re-uploading the same file
        if (list.length === 0) return;
        for (const f of list) {
            try {
                setUploadingPct(0);
                const created = await uploadProjectFile(
                    projectId, f, setUploadingPct, meta.kindForUpload,
                );
                setFiles((prev) => [created, ...prev]);
                toast.success(`Uploaded ${f.name}`);
            } catch (err) {
                toast.error(err?.response?.data?.detail || `Couldn't upload ${f.name}`);
            } finally {
                setUploadingPct(null);
            }
        }
    };

    const handleOpen = async (file) => {
        setBusyId(file.id);
        try {
            const url = await fetchProjectFileBlobUrl(projectId, file.id);
            window.open(url, "_blank", "noopener,noreferrer");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't open file");
        } finally {
            setBusyId(null);
        }
    };

    const handleDownload = async (file) => {
        setBusyId(file.id);
        try {
            const url = await fetchProjectFileBlobUrl(projectId, file.id);
            const a = document.createElement("a");
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't download");
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (file) => {
        if (!window.confirm(`Delete "${file.name}"? This can't be undone.`)) return;
        setBusyId(file.id);
        try {
            await deleteProjectFile(projectId, file.id);
            setFiles((prev) => prev.filter((f) => f.id !== file.id));
            toast.success("Deleted");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't delete");
        } finally {
            setBusyId(null);
        }
    };

    const HeaderIcon = meta.Icon;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="bg-[#05091A] border-l border-[#2A3655] text-white w-full sm:max-w-md flex flex-col"
                data-testid={`project-files-sheet-${kind}`}
            >
                <SheetHeader className="text-left">
                    <SheetTitle className="text-white flex items-center gap-2">
                        <HeaderIcon className="w-5 h-5 text-[#00A3FF]" />
                        {meta.title}
                    </SheetTitle>
                    <SheetDescription className="text-slate-400">
                        {meta.description}
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-4 space-y-2">
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={meta.accept || undefined}
                        className="hidden"
                        onChange={handleUpload}
                        data-testid={`project-files-input-${kind}`}
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingPct != null}
                        data-testid={`project-files-upload-btn-${kind}`}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm border-2 border-dashed border-[#2A3655] hover:border-[#00A3FF] hover:bg-[#00A3FF]/5 text-slate-300 hover:text-[#00A3FF] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {uploadingPct != null ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="text-xs font-bold uppercase tracking-wider">
                                    Uploading {uploadingPct}%
                                </span>
                            </>
                        ) : (
                            <>
                                <Upload className="w-4 h-4" />
                                <span className="text-xs font-bold uppercase tracking-wider">
                                    {meta.uploadLabel}
                                </span>
                            </>
                        )}
                    </button>
                    {/* iter-100: manual expense entry — only on Receipts */}
                    {meta.allowManual && (
                        <button
                            type="button"
                            onClick={() => setManualOpen(true)}
                            disabled={uploadingPct != null}
                            data-testid="project-files-add-manual-expense-btn"
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm border border-[#2A3655] hover:border-emerald-500/60 hover:bg-emerald-500/5 text-slate-300 hover:text-emerald-300 transition-colors text-xs font-bold uppercase tracking-wider"
                        >
                            <Plus className="w-4 h-4" />
                            Add expense (no receipt)
                        </button>
                    )}
                </div>

                <div
                    className="flex-1 min-h-0 mt-4 overflow-y-auto -mx-4 px-4"
                    data-testid={`project-files-list-${kind}`}
                >
                    {loading && files.length === 0 && (
                        <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Loading…
                        </div>
                    )}
                    {!loading && files.length === 0 && (
                        <div className="text-center py-12 text-slate-500 text-sm">
                            {meta.emptyText}
                        </div>
                    )}
                    {meta.layout === "grid" ? (
                        <div className="grid grid-cols-2 gap-2">
                            {files.map((f) => (
                                <ThumbCard
                                    key={f.id}
                                    file={f}
                                    thumbUrl={thumbs[f.id]}
                                    busy={busyId === f.id}
                                    onOpen={() => handleOpen(f)}
                                    onDownload={() => handleDownload(f)}
                                    onDelete={() => handleDelete(f)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-1.5">
                            {files.map((f) => {
                                const Icon = f.manual ? DollarSign : fileIcon(f.mime);
                                const isBusy = busyId === f.id;
                                return (
                                    <div
                                        key={f.id}
                                        className="group/file flex items-center gap-2 p-2 rounded-sm border border-[#2A3655]/50 hover:border-[#00A3FF]/40 hover:bg-[#0A1128]/80 transition-colors"
                                        data-testid={`project-file-${f.id}`}
                                    >
                                        <Icon className={`w-5 h-5 shrink-0 ${f.manual ? "text-emerald-400" : "text-[#00A3FF]"}`} />
                                        <div className="flex-1 min-w-0">
                                            <div
                                                className="text-sm text-white truncate"
                                                title={f.title || f.name}
                                            >
                                                {f.title || f.name}
                                                {f.manual && (
                                                    <span className="ml-1.5 text-[9px] uppercase tracking-wider text-emerald-300/80 font-bold">
                                                        manual
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[10px] text-slate-500 truncate">
                                                {f.manual
                                                    ? `${fmtMoney(f.amount, f.currency)} · ${f.expense_date || fmtDate(f.created_at)}`
                                                    : `${fmtSize(f.size)} · ${fmtDate(f.created_at)}`
                                                }
                                                {f.kind && f.kind !== "file" ? ` · ${f.kind}` : ""}
                                                {f.uploaded_by_email ? ` · ${f.uploaded_by_email}` : ""}
                                            </div>
                                        </div>
                                        <div className="shrink-0 flex items-center gap-0.5">
                                            {!f.manual && (
                                                <>
                                                    <FileActionBtn
                                                        onClick={() => handleOpen(f)}
                                                        disabled={isBusy}
                                                        testId={`project-file-${f.id}-open`}
                                                        title="Open in new tab"
                                                        Icon={ExternalLink}
                                                        hoverColor="text-[#00A3FF]"
                                                        busy={isBusy}
                                                    />
                                                    <FileActionBtn
                                                        onClick={() => handleDownload(f)}
                                                        disabled={isBusy}
                                                        testId={`project-file-${f.id}-download`}
                                                        title="Download"
                                                        Icon={Download}
                                                        hoverColor="text-emerald-400"
                                                    />
                                                </>
                                            )}
                                            <FileActionBtn
                                                onClick={() => handleDelete(f)}
                                                disabled={isBusy}
                                                testId={`project-file-${f.id}-delete`}
                                                title="Delete"
                                                Icon={Trash2}
                                                hoverColor="text-red-400"
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </SheetContent>
            {/* iter-100: Manual expense entry dialog. Rendered inside
                the Sheet so it auto-closes when the drawer closes. */}
            <ManualExpenseDialog
                open={manualOpen}
                onOpenChange={setManualOpen}
                onSubmit={async (payload) => {
                    try {
                        const created = await addManualExpense(
                            projectId, payload, meta.kindForUpload || "receipt",
                        );
                        setFiles((prev) => [created, ...prev]);
                        toast.success("Expense added");
                        setManualOpen(false);
                    } catch (err) {
                        toast.error(
                            err?.response?.data?.detail || "Couldn't add expense",
                        );
                    }
                }}
            />
        </Sheet>
    );
}

function ManualExpenseDialog({ open, onOpenChange, onSubmit }) {
    const todayIso = new Date().toISOString().slice(0, 10);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [amount, setAmount] = useState("");
    const [expenseDate, setExpenseDate] = useState(todayIso);
    const [submitting, setSubmitting] = useState(false);

    // Reset form whenever the dialog opens so a previous entry's stale
    // values don't leak into the next one.
    useEffect(() => {
        if (open) {
            setTitle("");
            setDescription("");
            setAmount("");
            setExpenseDate(todayIso);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const submit = async (e) => {
        e.preventDefault();
        const t = title.trim();
        const a = parseFloat(amount);
        if (!t) {
            toast.error("Title is required");
            return;
        }
        if (Number.isNaN(a)) {
            toast.error("Amount must be a number");
            return;
        }
        setSubmitting(true);
        try {
            await onSubmit({
                title: t,
                description: description.trim(),
                amount: a,
                expense_date: expenseDate,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#0A1128] border border-[#2A3655] text-white max-w-md"
                data-testid="manual-expense-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-emerald-400" />
                        Add expense
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold block mb-1">
                            Title <span className="text-red-400">*</span>
                        </label>
                        <input
                            autoFocus
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g. Fuel — pickup truck"
                            data-testid="manual-expense-title"
                            className="w-full bg-[#05091A] border border-[#2A3655] rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#00A3FF]"
                            required
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold block mb-1">
                                Amount <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0.00"
                                data-testid="manual-expense-amount"
                                className="w-full bg-[#05091A] border border-[#2A3655] rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#00A3FF] tabular-nums"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold block mb-1">
                                Date
                            </label>
                            <input
                                type="date"
                                value={expenseDate}
                                onChange={(e) => setExpenseDate(e.target.value)}
                                data-testid="manual-expense-date"
                                className="w-full bg-[#05091A] border border-[#2A3655] rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00A3FF]"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold block mb-1">
                            Description (optional)
                        </label>
                        <textarea
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What was this for?"
                            data-testid="manual-expense-description"
                            className="w-full bg-[#05091A] border border-[#2A3655] rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#00A3FF] resize-none"
                        />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#2A3655]">
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                            className="px-4 py-2 rounded-sm border border-[#2A3655] text-slate-300 hover:border-slate-500 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            data-testid="manual-expense-save"
                            className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold h-9 px-4 disabled:opacity-50"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                                    Saving…
                                </>
                            ) : (
                                "Save expense"
                            )}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ThumbCard({ file, thumbUrl, busy, onOpen, onDownload, onDelete }) {
    const Icon = fileIcon(file.mime);
    const isImage = (file.mime || "").startsWith("image/");
    // iter-100: manual expense rows have no blob — render an amount +
    // date card instead of a thumbnail. No Open/Download buttons either.
    if (file.manual) {
        return (
            <div
                className="group/thumb relative rounded-sm border border-emerald-500/30 bg-emerald-500/5 overflow-hidden hover:border-emerald-400/70 transition-colors"
                data-testid={`project-file-thumb-${file.id}`}
            >
                <div className="aspect-square flex flex-col items-center justify-center px-2 text-center">
                    <DollarSign className="w-6 h-6 text-emerald-400 mb-1" />
                    <div className="text-lg font-bold text-white tabular-nums">
                        {fmtMoney(file.amount, file.currency)}
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-emerald-200/80 mt-1">
                        <CalendarDays className="w-3 h-3" />
                        {file.expense_date || fmtDate(file.created_at)}
                    </div>
                    <div
                        className="text-[10px] text-slate-400 italic mt-1 line-clamp-2 px-1"
                        title="Manual expense — no receipt"
                    >
                        no receipt
                    </div>
                </div>
                <div className="px-2 py-1.5 border-t border-emerald-500/30 bg-[#0A1128]/80">
                    <div className="text-[11px] text-white truncate" title={file.title || file.name}>
                        {file.title || file.name}
                    </div>
                    {file.description && (
                        <div className="text-[9px] text-slate-500 truncate mt-0.5">
                            {file.description}
                        </div>
                    )}
                </div>
                <div className="absolute top-1 right-1 flex items-center gap-0.5 opacity-0 group-hover/thumb:opacity-100 transition-opacity bg-black/60 backdrop-blur-sm rounded-sm">
                    <FileActionBtn
                        onClick={onDelete}
                        disabled={busy}
                        testId={`project-file-${file.id}-delete`}
                        title="Delete"
                        Icon={Trash2}
                        hoverColor="text-red-400"
                    />
                </div>
                {busy && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                    </div>
                )}
            </div>
        );
    }
    return (
        <div
            className="group/thumb relative rounded-sm border border-[#2A3655]/60 bg-[#0A1128]/60 overflow-hidden hover:border-[#00A3FF]/60 transition-colors"
            data-testid={`project-file-thumb-${file.id}`}
        >
            <button
                type="button"
                onClick={onOpen}
                disabled={busy}
                className="block w-full aspect-square bg-[#05091A] cursor-pointer disabled:cursor-wait"
                aria-label={`Open ${file.name}`}
                data-testid={`project-file-${file.id}-open`}
            >
                {isImage && thumbUrl ? (
                    <img
                        src={thumbUrl}
                        alt={file.name}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-500">
                        <Icon className="w-10 h-10 text-[#00A3FF]" />
                    </div>
                )}
            </button>
            <div className="px-2 py-1.5 border-t border-[#2A3655]/60 bg-[#0A1128]/80">
                <div className="text-[11px] text-white truncate" title={file.name}>
                    {file.name}
                </div>
                <div className="text-[9px] text-slate-500 truncate mt-0.5">
                    {fmtDate(file.created_at)} · {fmtSize(file.size)}
                </div>
            </div>
            <div className="absolute top-1 right-1 flex items-center gap-0.5 opacity-0 group-hover/thumb:opacity-100 transition-opacity bg-black/60 backdrop-blur-sm rounded-sm">
                <FileActionBtn
                    onClick={onDownload}
                    disabled={busy}
                    testId={`project-file-${file.id}-download`}
                    title="Download"
                    Icon={Download}
                    hoverColor="text-emerald-400"
                />
                <FileActionBtn
                    onClick={onDelete}
                    disabled={busy}
                    testId={`project-file-${file.id}-delete`}
                    title="Delete"
                    Icon={Trash2}
                    hoverColor="text-red-400"
                />
            </div>
            {busy && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <Loader2 className="w-5 h-5 animate-spin text-[#00A3FF]" />
                </div>
            )}
        </div>
    );
}

function FileActionBtn({ onClick, disabled, testId, title, Icon, hoverColor, busy }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
            title={title}
            aria-label={title}
            className={`p-1.5 rounded-sm text-slate-400 ${hoverColor ? `hover:${hoverColor}` : "hover:text-white"} hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors`}
        >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
        </button>
    );
}
